"use client";

import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import "../onboarding/onboarding.css";
import "./auth.css";
import { AuthForm, type AuthMode } from "./auth-form";

interface AuthClientProps {
  defaultMode: AuthMode;
}

/**
 * Split-screen shell for /login and /signup. Left half = brand headline +
 * lede on a pure-black canvas; right half = a unified Sign In / Create
 * Account form. Mode toggles in-page (no navigation), but reflects ?mode=
 * so deep-links from /signup land directly on Create Account and links
 * from /login land on Sign In.
 */
export function AuthClient({ defaultMode }: AuthClientProps) {
  const t = useTranslations("auth");
  const searchParams = useSearchParams();
  const queryMode = searchParams.get("mode");
  const initialMode: AuthMode =
    queryMode === "signup" || queryMode === "signin"
      ? (queryMode as AuthMode)
      : defaultMode;
  const [mode, setMode] = useState<AuthMode>(initialMode);
  // Set by AuthForm when a successful auth call has triggered a browser
  // navigation (post-signin or OAuth-redirect). Drives a full-card
  // overlay so the tabs, fields, and submit button can't fire again
  // during the brief window before the next page paints.
  const [redirecting, setRedirecting] = useState(false);

  // Keep the URL in sync when the user toggles in-page so a refresh keeps
  // them on the same form, and so back/forward navigation between the two
  // modes feels like distinct destinations.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mode") === mode) return;
    params.set("mode", mode);
    const next = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, "", next);
  }, [mode]);

  return (
    <div className="ob-root auth-shell" data-theme="dark">
      <aside className="auth-aside">
        <div className="ob-mark auth-aside-mark">
          {t("brandPrefix")} <span className="dot">·</span> {t("brandSuffix")}
        </div>
        <h1 className="auth-headline ob-display-sm">
          <span className="char-rise">
            <span style={{ animationDelay: "0.05s" }}>
              {t("aside.headingLine1")}
            </span>
          </span>
          <br />
          <span className="char-rise">
            <span style={{ animationDelay: "0.18s" }} className="yellow">
              {t("aside.headingLine2")}
            </span>
          </span>
        </h1>
        <p className="ob-lede fade-up" style={{ animationDelay: "0.35s" }}>
          {t("aside.ledeBefore")}
          <strong style={{ color: "var(--fg)" }}>{t("aside.ledeBrand")}</strong>
          {t("aside.ledeAfter")}
        </p>
      </aside>

      <main className="auth-main">
        <div
          className={`auth-card ${redirecting ? "auth-card-redirecting" : ""}`}
          aria-busy={redirecting || undefined}
        >
          <div
            className="auth-tabs"
            role="tablist"
            aria-label={t("tabs.ariaLabel")}
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signin"}
              className={`auth-tab ${mode === "signin" ? "active" : ""}`}
              onClick={() => setMode("signin")}
              disabled={redirecting}
            >
              {t("tabs.signin")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signup"}
              className={`auth-tab ${mode === "signup" ? "active" : ""}`}
              onClick={() => setMode("signup")}
              disabled={redirecting}
            >
              {t("tabs.signup")}
            </button>
            <span
              className={`auth-tab-thumb ${mode === "signup" ? "right" : "left"}`}
              aria-hidden="true"
            />
          </div>
          <AuthForm mode={mode} onRedirectingChange={setRedirecting} />
          {redirecting && (
            <div className="auth-redirect-overlay" aria-live="polite">
              <span
                className="ob-btn-spinner auth-redirect-spinner"
                aria-hidden
              />
              <span className="auth-redirect-copy">
                {mode === "signin"
                  ? t("redirecting.signin")
                  : t("redirecting.signup")}
              </span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
